import type { MediaImage } from '@efdigital/ll-addon-data';
import type { MediaImageSizeType } from '@efdigital/ll-admin-v2/lib/media/models';
import {
  type ImageContent,
  type SizePreset,
} from '@efdigital/ll-admin-v2/lib/media/sizes';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import BsModal from 'ember-bootstrap/components/bs-modal';

import type MediaImagesService from '../../../services/media-images';
import { EditMediaImage } from '../../media/edit-media-image.gts';
import { ImageBrowser } from '../../media/image-browser.gts';
import { ImageUploader } from '../../media/image-uploader.gts';

interface ImagePickerModalSignature {
  Element: HTMLDivElement;
  Args: {
    requiredSize: MediaImageSizeType | 'original';
    customSize?: SizePreset;
    onSelectImage: (
      imageContent: ImageContent | null,
      mediaImage?: MediaImage
    ) => void;
  };
  Blocks: {
    input: [showModal: () => void];
  };
}

function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

export class ImagePickerModal extends Component<ImagePickerModalSignature> {
  @service('media-images') declare mediaImages: MediaImagesService;
  @tracked isOpen = false;
  @service declare store: Store;
  @tracked mode: 'browser' | 'upload' = 'browser';
  @tracked uploadedImage: MediaImage | null = null;

  get destinationElement(): HTMLElement {
    return document.body;
  }

  switchToBrowser = (): void => {
    this.mode = 'browser';
  };

  switchToUpload = (): void => {
    this.mode = 'upload';
  };

  showModal = (): void => {
    this.mode = 'browser';
    this.uploadedImage = null;
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  imageUploaded = (image: MediaImage): void => {
    this.uploadedImage = image;
  };

  /**
   * Selects an image and given size.
   * It will change the image picker mode to 'preview' and call the callback.
   * @param image MediaImage record
   * @param size The size of the image
   * @param customSize Custom dimensions (for 'custom' size)
   */
  imageSelected = async (image: MediaImage): Promise<void> => {
    // First ensure the size exists (will generate if needed)
    const content = await this.mediaImages.ensureSize(
      image,
      this.args.requiredSize,
      this.args.customSize
    );

    console.log('content', content);

    if (!content) {
      return;
    }

    this.args.onSelectImage(content, image);
    this.modalClosed();
  };

  get previewImageStyle() {
    return htmlSafe('max-height: 400px;');
  }

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <div class="text-center flex-grow-1">
          <div class="btn-group">
            <button
              type="button"
              class="btn btn-sm btn-primary
                {{if (eq this.mode 'browser') 'active'}}"
              {{on "click" this.switchToBrowser}}
            >Browse Images</button>
            <button
              type="button"
              class="btn btn-sm btn-primary
                {{if (eq this.mode 'upload') 'active'}}"
              {{on "click" this.switchToUpload}}
            >Upload Image</button>
          </div>
        </div>
      </modal.header>
      <modal.body>
        {{#if (eq this.mode "browser")}}
          <ImageBrowser @onImageSelected={{this.imageSelected}}>
            <:item as |item|>
              <div class="col-md-4">
                <div
                  class="card h-100 shadow-sm border-0 ll-image-browser-card"
                >
                  <div
                    class="position-relative overflow-hidden ll-image-browser-thumbnail-container"
                  >
                    <img
                      src={{item.thumbnailUrl}}
                      class="card-img-top h-100 w-100 object-fit-cover"
                      alt={{item.alt}}
                      role="button"
                      {{on "click" (fn this.imageSelected item)}}
                    />
                  </div>
                  <div class="card-body">
                    <h6
                      class="card-title mb-1 fw-semibold text-truncate"
                    >{{item.title}}</h6>
                    <p
                      class="card-text text-muted small mb-2 text-truncate"
                    >{{item.alt}}</p>
                  </div>
                  <div class="card-footer bg-white border-0 pt-0 d-grid gap-2">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary w-100"
                      {{on "click" (fn this.imageSelected item)}}
                    >
                      <iconify-icon
                        inline
                        icon="ph:check-circle-duotone"
                        class="me-1"
                      />
                      Select
                    </button>
                  </div>
                </div>
              </div>
            </:item>
          </ImageBrowser>
        {{else if (eq this.mode "upload")}}
          {{#if this.uploadedImage}}
            <div class="text-center mb-4">
              <img
                src={{this.uploadedImage.original.url}}
                alt="Preview"
                class="img-fluid rounded shadow-sm"
                style={{this.previewImageStyle}}
              />
            </div>

            <EditMediaImage
              @mediaImage={{this.uploadedImage}}
              @onSave={{this.imageSelected}}
            />
          {{else}}
            <ImageUploader @onImageUploaded={{this.imageUploaded}} />
          {{/if}}
        {{/if}}
      </modal.body>
    </BsModal>
  </template>
}
